import React from 'react'
import { Dropdown, Offcanvas } from 'react-bootstrap'
import useIndexBank from '../../../services/properties/hooks/useIndexBank';
import { PropertyBank } from '../../../services/properties/response/IndexBankResponse';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import FormError from '../../../shared/components/errors/FormError';
import { useDeleteBank } from '../../../services/properties/hooks/useDeleteBank';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import RequireAuth from '../../../auth/middleware/RequireAuth';

interface BankAccountDialogProps {
    show: boolean;
    onClose: () => void;
    propertyId: string;
    onCreate: () => void;
    onUpdate: (bankId: string) => void;
}

const BankAccountDialog: React.FC<BankAccountDialogProps> = ({ show, onClose, propertyId, onCreate, onUpdate }) => {
    const {
        data: bankAccounts,
        isSuccess,
        isError,
        isFetching
    } = useIndexBank({ filters: { property_id: propertyId } });

    const { mutate, isPending } = useDeleteBank();

    const queryClient = useQueryClient();

    const onRemove = (bankAccount: PropertyBank) => {
        toast.loading('Removing bank account...', { id: 'remove-bank-account' });

        mutate({ id: bankAccount.id }, {
            onSuccess: async () => {
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['banks'].includes(key as string))
                });

                toast.success('Bank account removed successfully', { id: 'remove-bank-account' });
            },
            onError: (mutationError) => {
                toast.error(mutationError?.message || 'Failed to remove bank account', { id: 'remove-bank-account' });
            }
        });
    }

    return (
        <Offcanvas show={show} onHide={onClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Bank Accounts</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {/* Tambahkan title dan tombol untuk create dengan display flex between */}
                <div className="mb-2">
                    <button className="btn btn-primary" onClick={onCreate}>Create</button>
                </div>
                <table className="table table-hover table-vcenter">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Account Number</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isError && (<FormError />)}
                        {isFetching && (
                            <div className="d-flex align-items-center justify-content-center">
                                <FormSpinner />
                            </div>
                        )}
                        {isSuccess && !isFetching && (
                            <>
                                {bankAccounts.data.length > 0 ? bankAccounts.data.map((bankAccount: PropertyBank, index: number) => (
                                    <React.Fragment key={index}>
                                        <tr>
                                            <td>
                                                {bankAccount.bank_name} ({bankAccount.account_name})
                                            </td>
                                            <td>
                                                {bankAccount.account_number}
                                            </td>
                                            <td>
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="default" id="dropdownMenuButton1">
                                                        Actions
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['bank_account_update']} asComponent>
                                                            <Dropdown.Item href="#" onClick={() => onUpdate(bankAccount.id)} disabled={isPending}>Update</Dropdown.Item>
                                                        </RequireAuth>
                                                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['bank_account_delete']} asComponent>
                                                            <Dropdown.Item href="#" onClick={() => onRemove(bankAccount)} className='text-danger' disabled={isPending}>Remove</Dropdown.Item>
                                                        </RequireAuth>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                )) : (
                                    <tr>
                                        <td colSpan={3}>No bank accounts found</td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default BankAccountDialog